import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, Lock, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { API, createOrder, submitToRedsys } from "../lib/api";
import { CheckoutForm, validate } from "../components/checkout/CheckoutForm";
import { MethodPicker } from "../components/checkout/MethodPicker";
import { OrderSummary } from "../components/checkout/OrderSummary";
import { PayPalCheckout } from "../components/PayPalCheckout";
import { useCustomer } from "../context/CustomerContext";

const KEY = "jungle-checkout-customer";
const load = () => { try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch { return {}; } };
const LABEL = { card: "Pagar con tarjeta", bizum: "Pagar con Bizum", transfer: "Confirmar pedido" };

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const nav = useNavigate();
  const [customer, setCustomer] = useState(load);
  const [errors, setErrors] = useState({});
  const [method, setMethod] = useState("card");
  const [paypal, setPaypal] = useState(false);
  const [busy, setBusy] = useState(false);
  const shipping = subtotal >= 100 ? 0 : 10.95;
  const { user } = useCustomer();

  useEffect(() => {
    if (!user) return undefined;
    setCustomer((c) => ({ ...c, name: user.name, email: user.email, phone: user.phone, dni: user.dni, line1: user.line1, postal_code: user.postal_code, city: user.city, state: user.state }));
    return undefined;
  }, [user]);

  useEffect(() => { try { localStorage.setItem(KEY, JSON.stringify(customer)); } catch { /* blocked */ } }, [customer]);
  useEffect(() => {
    if (!API) return undefined;
    axios.get(`${API}/paypal/config`).then((r) => setPaypal(!!r.data?.enabled)).catch(() => setPaypal(false));
    return undefined;
  }, []);

  const validCustomer = () => {
    const e = validate(customer);
    setErrors(e);
    if (Object.keys(e).length) { toast.error("Revisa los datos de envío."); return null; }
    return { ...customer, business: customer.business || null, line2: customer.line2 || null };
  };

  const pay = async () => {
    const c = validCustomer();
    if (!c) return;
    setBusy(true);
    try {
      const r = await createOrder(items, c, method);
      if (r.method === "transfer") { clear(); nav(`/pedido/transferencia?ref=${r.order_id}`); return; }
      submitToRedsys(r.action, r.fields);
    } catch (err) {
      toast.error(err?.response?.status === 422 ? "Revisa los datos de envío." : "No se pudo iniciar el pago. Inténtalo de nuevo.");
      setBusy(false);
    }
  };

  if (!items.length) {
    return (
      <main data-testid="checkout-empty" className="min-h-[80svh] grid place-items-center px-5 pt-24 text-center">
        <div><p className="font-serif-d text-4xl">Tu carrito está vacío.</p>
          <Link to="/" className="inline-flex mt-8 rounded-full bg-[#8CD05A] text-[#0E2A1C] font-semibold px-7 h-12 items-center">Volver a la tienda</Link></div>
      </main>
    );
  }
  return (
    <main data-testid="checkout-page" className="max-w-[1100px] mx-auto px-5 pt-28 pb-24">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#C9D6C2] hover:text-[#F2B441]"><ArrowLeft className="w-4 h-4" /> Seguir comprando</Link>
      <h1 className="font-serif-d font-black text-4xl sm:text-5xl mt-4 mb-4">Finalizar compra</h1>
      <p data-testid="checkout-account-hint" className="text-sm text-[#C9D6C2] mb-10">
        {user ? <>Comprando como <strong className="text-[#F6EEDC]">{user.email}</strong>. Hemos rellenado tus datos.</>
          : <>¿Ya eres cliente? <Link to="/cuenta?volver=compra" data-testid="checkout-login-link" className="underline underline-offset-4 text-[#F2B441]">Inicia sesión o crea tu cuenta</Link> y rellenamos tus datos. También puedes comprar sin cuenta.</>}
      </p>
      <div className="grid lg:grid-cols-[1fr_380px] gap-10 items-start">
        <div>
          <CheckoutForm value={customer} onChange={setCustomer} errors={errors} />
          <MethodPicker value={method} onChange={setMethod} paypal={paypal} />
          <div className="mt-8">
            {method === "paypal" ? <PayPalCheckout getCustomer={validCustomer} /> : (
              <button type="button" onClick={pay} disabled={busy} data-testid="checkout-pay-button"
                className="w-full h-14 rounded-full bg-[#8CD05A] text-[#0E2A1C] font-semibold hover:bg-[#F2B441] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} {LABEL[method]}
              </button>
            )}
            <p className="text-xs text-[#C9D6C2] mt-3 text-center">
              {method === "transfer" ? "Recibirás el IBAN por correo. Preparamos el envío al recibir la transferencia." : method === "paypal" ? "Rellena tus datos y pulsa el botón de PayPal para pagar con tu cuenta." : "Te llevamos a la pasarela segura de tu banco (Redsys). Nunca vemos los datos de tu tarjeta."}
            </p>
          </div>
        </div>
        <OrderSummary items={items} subtotal={subtotal} shipping={shipping} />
      </div>
    </main>
  );
}
